'use client';

import * as React from 'react';

import { Drawer as SheetPrimitive } from '@base-ui/react/drawer';
import { XIcon } from 'lucide-react';

import { Button } from '#components/button';
import { cn, resolveNativeButton } from '#lib/utils';

// PAY-777 — this Sheet is built on Base UI's `Drawer` (Portal > Viewport >
// Popup). It animates with tailwindcss-animate KEYFRAMES keyed on
// `data-open`/`data-closed` (the same mechanism the pre-Base-UI Radix sheet
// used), NOT CSS transitions.
//
// Why keyframes: a CSS animation plays whenever the element carries
// `data-open` — including the frame it MOUNTS. Many call-sites conditionally
// mount the sheet already-open (`{entity && <Sheet open />}`), and a
// transition only fires when `open` changes on an already-mounted element, so
// those sheets never slid. Keyframes slide on mount, so every sheet animates
// its enter regardless of how the call-site is wired.
//
// (Exit still animates for sheets that stay mounted while `open` toggles;
// sheets that unmount on close skip the exit — matching the old behaviour.)

type SheetSide = 'top' | 'right' | 'bottom' | 'left';

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  nativeButton,
  ...props
}: SheetPrimitive.Trigger.Props) {
  return (
    <SheetPrimitive.Trigger
      data-slot="sheet-trigger"
      nativeButton={nativeButton ?? resolveNativeButton(props.render)}
      {...props}
    />
  );
}

function SheetClose({ nativeButton, ...props }: SheetPrimitive.Close.Props) {
  return (
    <SheetPrimitive.Close
      data-slot="sheet-close"
      nativeButton={nativeButton ?? resolveNativeButton(props.render)}
      {...props}
    />
  );
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      // Docked Milo rises above any open modal backdrop (PAY-5625).
      data-modal-backdrop=""
      className={cn(
        'data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 z-50 bg-black/10 duration-300 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  closeLabel = 'Close',
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  showCloseButton?: boolean;
  /** The close button's accessible name; pass translated text. */
  closeLabel?: string;
}) {
  const isHorizontal = side === 'left' || side === 'right';

  // Viewport is a full-screen flex container that parks the Popup against the
  // requested edge. pointer-events-none lets clicks fall through to the
  // backdrop for dismissal; the Popup re-enables them.
  const viewportClassName = cn(
    'pointer-events-none fixed inset-0 z-50 flex overflow-hidden md:right-[var(--milo-dock-width,0px)]',
    side === 'right' && 'justify-end',
    side === 'left' && 'justify-start',
    side === 'top' && 'flex-col justify-start',
    side === 'bottom' && 'flex-col justify-end',
  );

  // Per-side border + the enter/exit slide keyframes (tailwindcss-animate).
  const slideClassName = cn(
    side === 'right' &&
      'data-open:slide-in-from-right data-closed:slide-out-to-right h-full border-l',
    side === 'left' &&
      'data-open:slide-in-from-left data-closed:slide-out-to-left h-full border-r',
    side === 'top' &&
      'data-open:slide-in-from-top data-closed:slide-out-to-top w-full border-b',
    side === 'bottom' &&
      'data-open:slide-in-from-bottom data-closed:slide-out-to-bottom w-full border-t',
  );

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Viewport className={viewportClassName}>
        <SheetPrimitive.Popup
          data-slot="sheet-content"
          data-side={side}
          className={cn(
            'bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out pointer-events-auto z-50 flex flex-col gap-4 bg-clip-padding p-6 text-sm shadow-lg duration-300 ease-in-out outline-none',
            slideClassName,
            // Width via plain classes (not per-side variants) so call-site
            // `sm:max-w-*` overrides merge and win.
            isHorizontal && 'w-3/4 sm:max-w-sm',
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close
              data-slot="sheet-close"
              render={
                <Button
                  variant="ghost"
                  className="absolute top-3 right-3"
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="sr-only">{closeLabel}</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetPrimitive.Viewport>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-0.5', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-foreground text-base font-medium', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
